import DOMPurify from 'dompurify';
import config from '@/config';

const outlookSafelinksTitle = `Links are hidden in ${config.appName} but can be clicked from the original email in your inbox.`;

const trackingMarkers = ['open', 'track', 'tracking', 'pixel', 'beacon'];
const hiddenStylePattern =
  /display\s*:\s*none|visibility\s*:\s*hidden|opacity\s*:\s*0(?:[; ]|$)/i;

function normalizeContentId(contentId: string): string {
  let normalized = contentId.trim().replace(/^<|>$/g, '');
  const atIndex = normalized.indexOf('@');
  if (atIndex !== -1) {
    normalized = normalized.substring(0, atIndex);
  }
  return normalized;
}

export function replaceCidUrls(
  content: string,
  cidMap: Map<string, string>,
): string {
  if (cidMap.size === 0) return content;

  return content.replace(
    /src=["']cid:([^"'@>]+)(?:@[^"'>]*)?\s*["']/gi,
    (match, contentId) => {
      const url = cidMap.get(normalizeContentId(contentId));
      if (url) {
        return `src="${url}"`;
      }
      return match;
    },
  );
}

function getStyleValue(styleText: string, property: string): string | null {
  const match = styleText.match(new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() ?? null;
}

function getDimensionValue(value: string | null): number | null {
  if (!value) return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === '') return null;
  if (normalized === '0') return 0;

  const numericPart = Number.parseFloat(normalized.replace(/px$/, ''));
  if (Number.isNaN(numericPart)) {
    return null;
  }

  return numericPart;
}

function getImageDimension(image: HTMLImageElement, name: 'width' | 'height') {
  const attributeValue = image.getAttribute(name);
  const styleValue = getStyleValue(image.getAttribute('style') ?? '', name);

  return getDimensionValue(attributeValue) ?? getDimensionValue(styleValue);
}

function hasTrackingMarker(image: HTMLImageElement): boolean {
  const haystack = [
    image.getAttribute('src') ?? '',
    image.getAttribute('id') ?? '',
    image.getAttribute('class') ?? '',
  ]
    .join(' ')
    .toLowerCase();

  return trackingMarkers.some((marker) => haystack.includes(marker));
}

function hasHiddenStyle(image: HTMLImageElement): boolean {
  return hiddenStylePattern.test(image.getAttribute('style') ?? '');
}

function hasTinyDimensions(image: HTMLImageElement): boolean {
  const width = getImageDimension(image, 'width');
  const height = getImageDimension(image, 'height');

  if (width === null || height === null) {
    return false;
  }

  return width <= 1 && height <= 1;
}

function isTrackingPixel(image: HTMLImageElement): boolean {
  const tinyImage = hasTinyDimensions(image);
  const hiddenImage = hasHiddenStyle(image);
  const suspiciousSource = hasTrackingMarker(image);

  return tinyImage || (hiddenImage && suspiciousSource);
}

function isOutlookSafelink(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname.endsWith('.safelinks.protection.outlook.com');
  } catch {
    return false;
  }
}

function rewriteOutlookSafelinks(document: Document) {
  const anchors = document.querySelectorAll('a');

  for (const anchor of anchors) {
    const href = anchor.getAttribute('href');
    if (!href || !isOutlookSafelink(href)) {
      continue;
    }

    const renderedText = anchor.textContent?.trim() ?? '';
    if (renderedText === href) {
      anchor.textContent = 'Open link in original email';
    }

    anchor.removeAttribute('href');
    anchor.setAttribute('title', outlookSafelinksTitle);
    anchor.setAttribute('aria-disabled', 'true');
    anchor.classList.add('cursor-not-allowed', 'opacity-80');
  }
}

function removeTrackingPixels(document: Document) {
  const images = document.querySelectorAll('img');

  for (const image of images) {
    if (isTrackingPixel(image)) {
      image.remove();
    }
  }
}

export function sanitizeProcessedHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['aria-disabled'],
  });
}

export function processEmailHtml({
  html,
  cidMap,
}: {
  html: string;
  cidMap?: Map<string, string>;
}): string {
  const contentWithResolvedCids =
    cidMap && cidMap.size > 0 ? replaceCidUrls(html, cidMap) : html;

  const parser = new DOMParser();
  const document = parser.parseFromString(contentWithResolvedCids, 'text/html');

  removeTrackingPixels(document);
  rewriteOutlookSafelinks(document);

  return sanitizeProcessedHtml(document.body.innerHTML);
}

export function getOutlookSafelinksTitle(): string {
  return outlookSafelinksTitle;
}
